// src/components/files/FileBreadcrumb.tsx
import React from 'react';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';

const FileBreadcrumb: React.FC = () => {
  const { t } = useTranslation();
  const { state, navigateToFolder, refreshContents } = useFileManager();
  const { breadcrumb, loading, section } = state;

  const handleHomeClick = () => {
    navigateToFolder(null);
  };

  const handleFolderClick = (folderId: number) => {
    navigateToFolder(folderId);
  };

  const handleRefresh = () => {
    refreshContents();
  };

  // Don't show breadcrumb for non-folder sections
  if (section !== 'my-space') {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1,
        px: 0,
      }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'grey.400' }} />}
        sx={{
          '& .MuiBreadcrumbs-ol': {
            alignItems: 'center',
          },
        }}
      >
        {/* Home / Root */}
        <Link
          component="button"
          underline="none"
          onClick={handleHomeClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: breadcrumb.length === 0 ? 'text.primary' : 'text.secondary',
            fontWeight: breadcrumb.length === 0 ? 600 : 500,
            fontSize: '0.9rem',
            cursor: 'pointer',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            transition: 'all 0.15s ease',
            '&:hover': {
              bgcolor: alpha('#2563eb', 0.08),
              color: 'primary.main',
            },
          }}
        >
          <HomeIcon sx={{ fontSize: 18 }} />
          {t('fileManager.breadcrumb.myFiles', 'Mes fichiers')}
        </Link>

        {/* Folder path */}
        {breadcrumb.map((item, index) => {
          const isLast = index === breadcrumb.length - 1;
          return isLast ? (
            <Typography
              key={item.id}
              sx={{
                color: 'text.primary',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 1,
                py: 0.5,
              }}
            >
              {item.name}
            </Typography>
          ) : (
            <Link
              key={item.id}
              component="button"
              underline="none"
              onClick={() => handleFolderClick(item.id)}
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.9rem',
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: alpha('#2563eb', 0.08),
                  color: 'primary.main',
                },
              }}
            >
              {item.name}
            </Link>
          );
        })}
      </Breadcrumbs>

      {/* Refresh button */}
      <Tooltip title={t('fileManager.toolbar.refresh', 'Actualiser')}>
        <IconButton
          size="small"
          onClick={handleRefresh}
          disabled={loading}
          sx={{
            color: 'grey.500',
            '&:hover': {
              bgcolor: 'grey.100',
              color: 'primary.main',
            },
          }}
        >
          <RefreshIcon
            sx={{
              fontSize: 20,
              animation: loading ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default FileBreadcrumb;
